'use client';

import React, { useMemo } from 'react';
import Link from 'next/link';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';

import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import WidgetHeader from 'src/components/Profiles/shared/widgets/WidgetHeader';
import { Card } from 'src/components/PulseUI';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { getProfileUrl } from 'src/utils/profileUrl';
import { getEntityIdentifiers } from 'src/utils/getEntityIdentifiers';

const MAX_ITEMS = 5;

const CompactProfessionalMix: React.FC = () => {
  const { entityDetails } = useProfile();
  const { closeDrawer } = useDrawer();

  const { providerId } = getEntityIdentifiers(entityDetails);
  const profileType = entityDetails?.type || 'center';

  const mixes: { title: string; value: number; filter_id: string }[] =
    entityDetails?.professional_mix || [];

  const sortedMixes = useMemo(
    () =>
      [...mixes]
        .sort((a, b) => a.title.localeCompare(b.title))
        .slice(0, MAX_ITEMS),
    [mixes]
  );

  return (
    <Card
      className="p-4 flex flex-col gap-2 bg-white h-full"
      data-testid="compact-professional-mix"
    >
      <WidgetHeader fallbackTitle="Healthcare Professional Mix" />

      <div className="flex-1 min-h-0 overflow-y-auto">
        {sortedMixes.length === 0 ? (
          <WidgetEmpty data-testid="compact-professional-mix-empty" variant="full" />
        ) : (
          <div
            className="flex flex-col"
            role="list"
            aria-label="Healthcare Professional Mix"
            data-testid="compact-professional-mix-list"
          >
            <div
              className="flex items-center justify-end pb-1 border-b border-neutral-100 sticky top-0 bg-white z-10"
              role="row"
              aria-label="Column headers"
            >
              <span
                className="text-xs font-semibold text-tertiary min-w-16 text-end"
                role="columnheader"
              >
                Count
              </span>
            </div>
            {sortedMixes.map((mix, index: number) => (
              <div
                key={`${mix.title}-${index}`}
                className="flex items-center justify-between py-1.5 border-b border-neutral-100 last:border-b-0"
                role="listitem"
              >
                <span className="text-sm font-medium text-primary truncate">
                  {mix.title}
                </span>
                <span className="text-sm font-semibold text-secondary flex-shrink-0 ml-2">
                  {mix.value}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {mixes.length > 0 && (
        <Link
          href={getProfileUrl(profileType, providerId, 'affiliations')}
          onClick={() => closeDrawer('profile')}
          className="flex items-center gap-1.5 text-sm font-medium text-sky-600 hover:text-sky-700 transition-colors"
          data-testid="compact-professional-mix-view-all"
        >
          View all affiliations
          <Icon icon={faArrowRight} className="h-3 w-3" />
        </Link>
      )}
    </Card>
  );
};

export default CompactProfessionalMix;
